import * as braze from '@braze/web-sdk';
import md5 from 'crypto-js/md5';
import { v4 as uuidv4 } from 'uuid';

import { Logger } from '@/utils/logger';
import type { User } from '@montugroup/circuit-shared/types/user';

import settings from '../constants/constants';

import type { Customer } from '@/hooks/user/useCustomer';

const logger = new Logger('braze.service');
// https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/setting_custom_attributes/
export function updateUserAttributes({ user, customer }: { user: User; customer: Customer }) {
  const brazeUser = braze.getUser();
  if (!brazeUser) {
    return;
  }

  brazeUser.setFirstName(user.firstName ?? '');
  brazeUser.setEmail(user.email ?? '');
  brazeUser.setPhoneNumber(customer.shippingAddress.phone ?? '');
  brazeUser.setHomeCity(customer.shippingAddress.city ?? '');
  brazeUser.setCustomUserAttribute('zip_code', customer.shippingAddress.zipCode ?? '');
}

type BrazeInitOptions = {
  options?: Omit<braze.InitializationOptions, 'baseUrl'>;
  contentCardsSubscriber?: (events: braze.ContentCards) => void;
};

export function changeUser(email: User['email']) {
  const identifier = md5(email.toLowerCase().trim()).toString();
  braze.changeUser(identifier);
}

export interface BrazeOrder {
  productId: string;
  price: number;
  currencyCode: string;
  quantity: number;
  purchaseProperties: {
    products: {
      product_id: number;
      product_name: string;
      price: number;
      quantity: number;
      remaining_units: number;
      is_out_of_stock: boolean;
      not_addable: boolean;
    }[];
  };
}

export function brazeTrackOrderPurchase(products: [] = []) {
  try {
    if (products.length < 1) {
      return;
    }

    const orderId = uuidv4();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    products.forEach((p: any) => {
      braze.logPurchase(p['Product Name'] || 'Unknown', p.Price || 0, 'AUD', p.Quantity || 0, {
        order_id: orderId,
        product_id: p['Product ID'] || 0,
        remaining_units: p['Remaining Units'] || 0,
        is_out_of_stock: p['Is Out of Stock'] || false,
        not_addable: p['Is Addable'] || true,
      });
    });
  } catch (e) {
    logger.error('Braze checkout event failed to send', e);
  }
}

// https://www.braze.com/docs/developer_guide/platform_integration_guides/web/initial_sdk_setup/#step-2-initialize-braze
export function init({ contentCardsSubscriber }: BrazeInitOptions = {}) {
  braze.initialize(settings.brazeApiKey, {
    baseUrl: settings.brazeBaseUrl,
  });
  braze.automaticallyShowInAppMessages();

  if (contentCardsSubscriber) {
    braze.subscribeToContentCardsUpdates(contentCardsSubscriber);
    braze.requestContentCardsRefresh();
  }

  braze.openSession();
}
